/**
 * AddWalletModal — F2 (v2-final Design-Umsetzung, wallets.html)
 *
 * Ersetzt den bisherigen Wallet-Connect-Signing-Flow (WalletConnectModal +
 * useWalletConnect, siehe Foundation-Bericht) durch den kanonischen
 * Add-Wallet-Flow aus assets/add-flows.js#addWalletModal: Adresse einfuegen →
 * Vorschau → zwei aktive Aktionen (eigene Wallet / nur beobachten).
 *
 * Welle 4b / WP-B (Dennis-Entscheid 15:40, siehe welle4b-packages.md): Wallet
 * per Adresse als EIGENE Wallet ist in v1 ohne Signatur moeglich — WP-A
 * erweitert `POST /api/v2/wallets` additiv um { address, owned: true, name }
 * OHNE Nonce/Signatur-Flow, parallel zum weiterhin bestehenden
 * Ed25519-Signatur-Pfad (connectWallet in ./api/walletApi.ts, fuer den
 * vollen Signing-Flow als separates Ticket nach Go-Live). Damit loest dieses
 * Paket die WP3-Zusammenlegung (04.09., "Import-Button entfernt, da ohne
 * Signatur unehrlich") wieder auf: das Modal bietet jetzt zwei ehrliche,
 * aktive Aktionen an.
 *
 * - `POST /api/v2/wallets` mit `owned: true` → eigene Wallet, kein Signieren
 *   noetig (v1-Paritaet). Hotspots dieser Wallet gelten als eigene, zaehlen
 *   ins Plan-Kontingent, Provisionsregeln sind moeglich.
 * - `POST /api/v2/wallets/watch` → beobachtete Wallet (owned: false), reiner
 *   Lesezugriff, zaehlt NICHT ins Kontingent.
 * - Beide Pfade stossen serverseitig automatisch den Hotspot-Import an
 *   (WalletReimportJob, siehe WP4-Review) — der Hinweistext gilt daher fuer
 *   beide Aktionen.
 * - `GET /api/v2/wallets/lookup` (Vorschau-Karte mit Hotspot-Count/Balance)
 *   existiert weiterhin nicht — die Vorschau zeigt daher nur die formatierte
 *   Adresse, keine erfundenen Zahlen.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { addWatchedWallet, addOwnedWallet } from './api/walletApi'
import { truncateAddress } from './walletUtils'
import { useAccount } from '../settings/useAccount'
import { QuotaMeters } from '../../layouts/SidebarQuotaMeters'
import { apiErrorCode, apiErrorStatus } from '../../lib/errors/apiErrorMessage'

export type AddWalletMode = 'owned' | 'watch'

type Props = {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  // Welle 4b / WP-B: welche der zwei Aktionen ist vorgewaehlt (primaerer
  // Button), wenn der Aufrufer bereits eine Absicht kennt (z.B. WelcomePage
  // mit zwei getrennten Kacheln). Default 'watch' — bestehende Aufrufer ohne
  // dieses Prop (WalletsList) verhalten sich unveraendert.
  initialMode?: AddWalletMode
}

// Solana-Adresse: 32-44 Zeichen Base58 (identische Grenze wie AddHotspotModal).
const SOLANA_ADDRESS_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/

export function AddWalletModal({ isOpen, onClose, onSuccess, initialMode }: Props) {
  const { t } = useTranslation('wallets')
  const { account } = useAccount()
  const [address, setAddress] = useState('')
  const [checkedAddress, setCheckedAddress] = useState<string | null>(null)
  const [addressError, setAddressError] = useState<string | null>(null)
  const [isWatching, setIsWatching] = useState(false)
  const [watchError, setWatchError] = useState<string | null>(null)
  const [isAddingOwned, setIsAddingOwned] = useState(false)
  const [ownedError, setOwnedError] = useState<string | null>(null)
  // Rein abgeleitet (kein State/Effect noetig): steuert nur, welcher der
  // zwei Aktions-Buttons unten primaer gestylt ist. Der Aufrufer (z.B.
  // WelcomePage mit zwei getrennten Kacheln) uebergibt initialMode bei jedem
  // Oeffnen frisch ueber das Prop — die Modal-Instanz bleibt zwischen
  // isOpen-Wechseln gemountet, aber `mode` muss dabei nie "nachgezogen"
  // werden, da er direkt aus dem aktuellen Prop-Wert berechnet wird.
  const mode: AddWalletMode = initialMode ?? 'watch'

  function handleClose() {
    setAddress('')
    setCheckedAddress(null)
    setAddressError(null)
    setWatchError(null)
    setOwnedError(null)
    onClose()
  }

  function handleLookup(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = address.trim()
    if (!SOLANA_ADDRESS_RE.test(trimmed)) {
      setAddressError(t('error.invalidAddress'))
      setCheckedAddress(null)
      return
    }
    setAddressError(null)
    setCheckedAddress(trimmed)
  }

  async function handleWatch() {
    if (!checkedAddress) return
    setIsWatching(true)
    setWatchError(null)
    setOwnedError(null)
    try {
      const result = await addWatchedWallet(checkedAddress)
      const status = result.response?.status
      if (status === 201 || status === 200) {
        handleClose()
        onSuccess()
      } else if (status === 409) {
        setWatchError(t('error.alreadyWatched'))
      } else if (status === 422) {
        setWatchError(t('error.invalidAddress'))
      } else {
        setWatchError(t('error.watchlistError'))
      }
    } catch (err) {
      // apiClient wirft bei HTTP >= 400 eine ApiError (lib/api-client.ts) —
      // der obige result.response?.status-Zweig greift damit im echten
      // Betrieb praktisch nie fuer Fehlerstatus, nur der Erfolgsfall laeuft
      // durch. Ergaenzt um den tatsaechlichen Fehlerpfad (Status aus der
      // geworfenen ApiError), analog zu handleAddOwned unten.
      const status = apiErrorStatus(err)
      if (status === 409) {
        setWatchError(t('error.alreadyWatched'))
      } else if (status === 422) {
        setWatchError(t('error.invalidAddress'))
      } else {
        setWatchError(t('error.genericError'))
      }
    } finally {
      setIsWatching(false)
    }
  }

  async function handleAddOwned() {
    if (!checkedAddress) return
    setIsAddingOwned(true)
    setOwnedError(null)
    setWatchError(null)
    try {
      const result = await addOwnedWallet(checkedAddress)
      const status = result.response?.status
      if (status === 201 || status === 200) {
        handleClose()
        onSuccess()
      } else if (status === 409) {
        setOwnedError(t('error.alreadyOwned'))
      } else if (status === 422) {
        setOwnedError(t('error.invalidAddress'))
      } else if (status === 402) {
        setOwnedError(t('error.quotaExceeded'))
      } else {
        setOwnedError(t('error.ownedError'))
      }
    } catch (err) {
      const status = apiErrorStatus(err)
      if (status === 409) {
        setOwnedError(t('error.alreadyOwned'))
      } else if (status === 422) {
        setOwnedError(t('error.invalidAddress'))
      } else if (status === 402 || apiErrorCode(err) === 'quota_exceeded') {
        setOwnedError(t('error.quotaExceeded'))
      } else {
        setOwnedError(t('error.ownedError'))
      }
    } finally {
      setIsAddingOwned(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-wallet-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose()
      }}
      data-testid="add-wallet-modal"
    >
      <div className="modal">
        <div className="modal-header">
          <h2 id="add-wallet-title">{t('addWallet.title')}</h2>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            aria-label={t('addWallet.closeLabel')}
            onClick={handleClose}
          >
            &times;
          </button>
        </div>

        <div className="modal-body">
          <p className="muted" style={{ marginTop: 0 }}>
            {t('addWallet.intro')}
          </p>

          <form onSubmit={handleLookup}>
            <div className="form-group">
              <label htmlFor="add-wallet-address">{t('addWallet.addressLabel')}</label>
              <div className="row" style={{ gap: '8px' }}>
                <input
                  id="add-wallet-address"
                  className={`input${addressError ? ' is-error' : ''}`}
                  type="text"
                  value={address}
                  onChange={(e) => {
                    setAddress(e.target.value)
                    setCheckedAddress(null)
                  }}
                  placeholder={t('addWallet.addressPlaceholder')}
                  autoFocus
                  style={{ flex: 1 }}
                  data-testid="add-wallet-address-input"
                />
                <button type="submit" className="btn btn-secondary" data-testid="add-wallet-lookup">
                  {t('addWallet.lookupButton')}
                </button>
              </div>
              {addressError && (
                <span className="field-error" role="alert">
                  {addressError}
                </span>
              )}
            </div>
          </form>

          {checkedAddress && (
            <div
              className="card card-pad"
              style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '10px' }}
            >
              <div className="row" style={{ gap: '10px' }}>
                <div
                  className="identity-img identity-img-sm"
                  style={{ background: 'var(--gradient-brand)' }}
                  aria-hidden="true"
                />
                <div className="mono" data-testid="add-wallet-preview-address">
                  {truncateAddress(checkedAddress)}
                </div>
              </div>

              <div
                className="row"
                role="group"
                aria-label={t('addWallet.modeLabel')}
                style={{ gap: '8px', alignItems: 'stretch' }}
                data-testid="add-wallet-mode-options"
              >
                <div
                  className="card card-pad"
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    border: `1px solid ${mode === 'owned' ? 'var(--primary)' : 'var(--border)'}`,
                  }}
                  data-testid="add-wallet-option-owned"
                >
                  <div style={{ fontWeight: 600, fontSize: 13 }}>
                    {t('addWallet.ownedOption.title')}
                  </div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                    {t('addWallet.ownedOption.description')}
                  </div>
                </div>
                <div
                  className="card card-pad"
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    border: `1px solid ${mode === 'watch' ? 'var(--primary)' : 'var(--border)'}`,
                  }}
                  data-testid="add-wallet-option-watch"
                >
                  <div style={{ fontWeight: 600, fontSize: 13 }}>
                    {t('addWallet.watchOption.title')}
                  </div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                    {t('addWallet.watchOption.description')}
                  </div>
                </div>
              </div>

              <div
                className="hint"
                style={{ fontSize: '11px' }}
                data-testid="add-wallet-watch-info"
              >
                {t('addWallet.watchInfo')}
              </div>

              <div className="row" style={{ gap: '8px' }}>
                <button
                  type="button"
                  className={mode === 'owned' ? 'btn btn-primary' : 'btn btn-secondary'}
                  onClick={() => {
                    void handleAddOwned()
                  }}
                  disabled={isAddingOwned || isWatching}
                  data-testid="add-wallet-owned"
                  style={{ flex: 1 }}
                >
                  {isAddingOwned ? t('addWallet.adding') : t('addWallet.ownedButton')}
                </button>
                <button
                  type="button"
                  className={mode === 'watch' ? 'btn btn-primary' : 'btn btn-secondary'}
                  onClick={() => {
                    void handleWatch()
                  }}
                  disabled={isAddingOwned || isWatching}
                  data-testid="add-wallet-watch"
                  style={{ flex: 1 }}
                >
                  {isWatching ? t('addWallet.watching') : t('addWallet.watchButton')}
                </button>
              </div>

              {(ownedError || watchError) && (
                <div className="field-error" role="alert" data-testid="add-wallet-error">
                  {ownedError ?? watchError}
                </div>
              )}

              {account?.hotspot_quota?.mobile && account?.hotspot_quota?.iot && (
                <QuotaMeters quota={account.hotspot_quota} />
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={handleClose}>
            {t('common:actions.cancel')}
          </button>
        </div>
      </div>
    </div>
  )
}
